import { computeVisibility, evaluateGroup } from './conditions.js';
import type { AnswerMap, AnswerValue, FieldDef, RepeaterValue, TemplateDefinition } from './types.js';
import { isBlankAnswer, validateAnswer, type ValueIssue } from './values.js';

export interface ProgressItem {
  sectionId: string;
  fieldId: string;
  rowId?: string;
  childFieldId?: string;
  label: string;
  issues?: ValueIssue[];
}

export interface SectionProgress {
  sectionId: string;
  visible: boolean;
  valid: number;
  total: number;
}

export interface FormProgress {
  /** valid applicable required leaf fields */
  valid: number;
  /** applicable required leaf fields */
  total: number;
  complete: boolean;
  missing: ProgressItem[];
  invalid: ProgressItem[];
  sections: SectionProgress[];
  visibleFieldIds: string[];
}

export interface ProgressOptions {
  /** When 'client', internal-audience fields are excluded from the denominator. */
  audience?: 'client' | 'all';
}

function isRequired(f: FieldDef, answers: AnswerMap, row?: Record<string, AnswerValue>): boolean {
  if (f.required) return true;
  if (f.requiredWhen) return evaluateGroup(f.requiredWhen, { answers, ...(row ? { row } : {}) });
  return false;
}

export function isPopulatedRow(parent: FieldDef, values: Record<string, AnswerValue>): boolean {
  return (parent.children ?? []).some((c) => c.type !== 'info' && !isBlankAnswer(values[c.id]));
}

/**
 * Completion formula: valid applicable required leaf fields / applicable required leaf fields.
 * Excludes hidden fields, hidden sections, internal fields (client audience) and unused
 * optional repeater rows. Required children in populated rows count.
 */
export function computeProgress(
  template: Pick<TemplateDefinition, 'sections'>,
  answers: AnswerMap,
  opts: ProgressOptions = {},
): FormProgress {
  const visibility = computeVisibility(template, answers);
  const missing: ProgressItem[] = [];
  const invalid: ProgressItem[] = [];
  const sections: SectionProgress[] = [];
  let valid = 0;
  let total = 0;

  for (const section of template.sections) {
    const sp: SectionProgress = { sectionId: section.id, visible: visibility.sections.has(section.id), valid: 0, total: 0 };
    sections.push(sp);
    if (!sp.visible) continue;
    for (const field of section.fields) {
      if (field.type === 'info') continue;
      if (!visibility.fields.has(field.id)) continue;
      if (opts.audience === 'client' && field.audience === 'internal') continue;
      const answer = answers[field.id];

      if (field.type === 'repeater') {
        const rows = (answer?.kind === 'value' ? (answer.value as RepeaterValue)?.rows ?? [] : []).filter((r) => !r.deletedAt);
        const populated = rows.filter((r) => isPopulatedRow(field, r.values));
        const repeaterRequired = isRequired(field, answers);
        const waivedOrNa = answer && (answer.kind === 'waived' || (answer.kind === 'na' && field.allowNa));
        if (repeaterRequired) {
          const minRows = Math.max(1, field.validation?.minRows ?? 1);
          sp.total++;
          total++;
          if (waivedOrNa || populated.length >= minRows) {
            sp.valid++;
            valid++;
          } else {
            missing.push({ sectionId: section.id, fieldId: field.id, label: `${field.label} (add at least ${minRows})` });
          }
        }
        if (waivedOrNa) continue;
        for (const row of populated) {
          const rowAnswers = row.values;
          for (const child of field.children ?? []) {
            if (child.type === 'info') continue;
            if (!evaluateGroup(child.visibleWhen, { answers, row: rowAnswers })) continue;
            if (opts.audience === 'client' && child.audience === 'internal') continue;
            const ca = rowAnswers[child.id];
            const req = isRequired(child, answers, rowAnswers);
            const issues = isBlankAnswer(ca) ? [] : validateAnswer(child, ca);
            const item = { sectionId: section.id, fieldId: field.id, rowId: row.id, childFieldId: child.id, label: `${field.label} › ${child.label}` };
            if (issues.length) invalid.push({ ...item, issues });
            if (!req) continue;
            sp.total++;
            total++;
            if (!isBlankAnswer(ca) && issues.length === 0) {
              sp.valid++;
              valid++;
            } else if (isBlankAnswer(ca)) missing.push(item);
          }
        }
        continue;
      }

      const req = isRequired(field, answers);
      const issues = isBlankAnswer(answer) ? [] : validateAnswer(field, answer);
      const item = { sectionId: section.id, fieldId: field.id, label: field.label };
      if (issues.length) invalid.push({ ...item, issues });
      if (!req) continue;
      sp.total++;
      total++;
      if (!isBlankAnswer(answer) && issues.length === 0) {
        sp.valid++;
        valid++;
      } else if (isBlankAnswer(answer)) missing.push(item);
    }
  }

  return {
    valid,
    total,
    complete: valid === total && invalid.length === 0,
    missing,
    invalid,
    sections,
    visibleFieldIds: [...visibility.fields],
  };
}
